import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { Stack, TextField, Autocomplete, CardContent, Button, InputLabel, Chip, Box, Typography, MenuItem } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { ErrorMessage, Form, FormikProvider, useFormik } from "formik";
import * as constants from 'src/utils/constants';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import apiInstance from 'src/utils/apiAuth';
import TextEditor from 'src/components/TextEditor';

function getMinStartDate() {
    // Calculate the current date and time in the format required for the input
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const hours = String(currentDate.getHours()).padStart(2, '0');
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const currentDatetime = `${year}-${month}-${day}T${hours}:${minutes}`;
    return currentDatetime;
}

export default function BatchSchedule(props) {
    const company_ids = props?.companyIDs;

    const [senderDetails, setSenderDetails] = useState([]);
    const getSenderDetails = () => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/sender-details?`;

        apiInstance.get(apiUrl, { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } })
            .then((response) => {
                setSenderDetails(response?.data?.data?.filter(obj => obj?.is_active));
            })
            .catch((e) => showErrorToast(e));
    }

    useEffect(() => {
        getSenderDetails();
    }, []);

    const getEmailTemplate = (type, value) => {
        let apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/email-template/`;

        apiInstance({
            method: "post",
            url: apiUrl,
            headers: {
                Authorization: "token " + localStorage.getItem("authToken"),
            },
            data: {'key': value},
        })
            .then((response) => {
                if (response?.data) { 
                    if (type === "EMAIL") formik.setFieldValue('body', response?.data);
                }
            })
            .catch((e) => showErrorToast(e));
    }

    const emailType = props?.type;
    useEffect(() => {
        if (emailType) {
            getEmailTemplate("EMAIL", emailType);
        }
    }, [emailType])

    const Schema = Yup.object().shape({
        campaign_name: Yup.string().required("Campaign name is required."),
        sender: Yup.string().required('Sender is required'),
        subject: Yup.string()
            .max(200, 'Subject must be at most 200 characters')
            .required('Subject is required'),
        body: Yup.string()
            .max(50000, 'Body must be at most 50000 characters')
            .required('Body is required'),
        datetime: Yup.string()
    });

    const formik = useFormik({
        initialValues: {
            campaign_name: '',
            sender: '',
            subject: '',
            body: '',
            datetime: '',
        },
        validationSchema: Schema,
        onSubmit: (formValues) => {
            submitForm(formValues);
        }
    });

    const submitForm = (formValues) => {
        const submitJourneyItem = (url, data) => {
            apiInstance({
                method: "post",
                url: url,
                data: data,
            })
                .then(function (response) {
                    if (response) {
                        showToast(response?.data?.detail, 'success', 5000);
                        window.location.reload(false);
                    }
                })
                .catch(function (error) {
                    showErrorToast(error, null, 5000);
                })
                .finally(() => {
                    setSubmitting(false);
                });
        };

        let data = {
            company_ids: company_ids,
            journey_item_id: emailType === "INITIAL OUTREACH" ? 2 : null,
            email_type: emailType,
            campaign_name: formValues.campaign_name,
            subject: formValues.subject,
            body: formValues.body,
            datetime: formValues.datetime ? formValues.datetime : new Date().toISOString(),
            sender_id: formValues.sender
        }
        submitJourneyItem("campaigns/", data);

    }

    const { errors, touched, values, setFieldValue, handleSubmit, handleChange, isSubmitting, getFieldProps, setSubmitting } = formik;

    return (
        <CardContent style={{padding: props?.matches ? 10 : '' }}>
            <FormikProvider value={formik}>
                <Form autoComplete="off" onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <Typography variant="" color="textSecondary" sx={{ mb: 1 }}>
                            Note: You can use the following placeholders in your template:
                            <ul style={{listStyle: 'none', paddingLeft: 0}}>
                                <li><code>{'{{company_name}}'}</code> – Replaced with the company’s name.</li>
                                <li><code>{'{{first_name}}'}</code> – Replaced with the recipient’s first name.</li>
                                <li><code>{'{{pitch_line}}'}</code> – Replaced with the company’s pitch line.</li>
                            </ul>
                        </Typography>
                        <Stack direction="column" spacing={2} width="100%">
                            <TextField
                                variant="outlined"
                                name="campaign_name"
                                fullWidth
                                required
                                label="Campaign Name"
                                value={values.campaign_name}
                                onChange={handleChange}
                                error={Boolean(touched.campaign_name && errors.campaign_name)}
                                helperText={touched.campaign_name && errors.campaign_name}
                            />
                            <ErrorMessage name="campaign_name">
                                {(msg) => (
                                    <span style={{ color: "#FF4842", fontSize: "14px" }}>{msg}</span>
                                )}
                            </ErrorMessage>
                            {senderDetails?.length > 0 && (
                                <>
                                    {senderDetails.some(item => !item.is_verified) && (
                                        <p style={{ color: "#FF4842", fontSize: "14px", marginBottom: "10px" }}>
                                            Some senders are not verified. Please go to sender details to verify them before you can select them.
                                        </p>
                                    )}
                                    <TextField
                                        fullWidth
                                        select
                                        label="From"
                                        required
                                        value={formik.values.sender}
                                        {...getFieldProps("sender")}
                                        size={props?.matches ? 'small' : 'medium'}
                                        InputLabelProps={{
                                            style: { fontSize: props?.matches ? '12px' : "inherit" }
                                        }}
                                        FormHelperTextProps={{
                                            style: { fontSize: props?.matches ? '10px' : '12px' }
                                        }}
                                        SelectProps={{
                                            style: { fontSize: props?.matches ? '12px' : 'inherit' }
                                        }}
                                    >
                                        {senderDetails?.map(item => (
                                            <MenuItem key={item.id} value={item.id} disabled={!item?.is_verified} style={{ fontSize: props?.matches ? '12px' : 'inherit' }}> {item.email} </MenuItem>
                                        ))}
                                    </TextField>
                                    <ErrorMessage name="sender">
                                        {(msg) => (
                                        <span style={{ color: "#FF4842", fontSize: "14px" }}>{msg}</span>
                                        )}
                                    </ErrorMessage>
                                </>
                            )}

                            <Stack direction="column" spacing={2} width="100%">
                                <TextField
                                    variant="outlined"
                                    name="subject"
                                    fullWidth
                                    required
                                    label="Subject"
                                    value={values.subject}
                                    onChange={handleChange}
                                    error={Boolean(touched.subject && errors.subject)}
                                    helperText={touched.subject && errors.subject}
                                />
                                <ErrorMessage name="subject">
                                    {(msg) => (
                                        <span style={{ color: "#FF4842", fontSize: "14px" }}>{msg}</span>
                                    )}
                                </ErrorMessage>

                                <TextEditor
                                    name="body"
                                    value={values.body}
                                    onChange={(value) => setFieldValue('body', value)}
                                    minheight="17vh"
                                    placeholder="Body"
                                />
                                <ErrorMessage name="body">
                                    {(msg) => (
                                    <span style={{ color: "#FF4842", fontSize: "14px" }}>{msg}</span>
                                    )}
                                </ErrorMessage>

                                <TextField
                                    fullWidth
                                    label="Date"
                                    type="datetime-local"
                                    inputFormat="yyyy-MM-dd HH:mm"
                                    size={props?.matches ? 'small' : 'medium'}
                                    inputProps={{
                                        min: getMinStartDate(),
                                        style: { fontSize: props?.matches ? '12px' : 'inherit' }
                                    }}
                                    {...getFieldProps("datetime")}
                                    error={Boolean(touched.datetime && errors.datetime)}
                                    helperText={
                                        touched.datetime && errors.datetime
                                            ? errors.datetime
                                            : "If no date is selected, the campaign will start immediately."
                                    }
                                    InputLabelProps={{
                                        style: { fontSize: props?.matches ? '12px' : "inherit" },
                                        shrink: true,
                                    }}
                                    FormHelperTextProps={{
                                        style: { fontSize: props?.matches ? '10px' : '12px' }
                                    }}
                                    SelectProps={{
                                        style: { fontSize: props?.matches ? '12px' : 'inherit' }
                                    }}
                                >
                                </TextField>
                            </Stack>
                        </Stack>

                        <Stack spacing={2}>
                            <LoadingButton
                                fullWidth
                                sx={{ mt: 2 }}
                                size="medium"
                                color="error"
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                                style={{ fontSize: props?.matches ? '12px' : "inherit" }}
                            >
                                Send Email
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </Form>
            </FormikProvider>
        </CardContent>
    )
}